import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider } from './context/AuthContext';
import { StoreProvider, useStore } from './context/StoreContext';
import { TrackingProvider } from './context/TrackingContext';

// 前台頁面
import Home from './pages/Home';
import ProductList from './pages/ProductList';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderComplete from './pages/OrderComplete';
import OrderStatus from './pages/OrderStatus';
import ShoppingGuide from './pages/ShoppingGuide';
import Login from './pages/Login';
import MemberCenter from './pages/MemberCenter';
import Apply from './pages/Apply';
import ApplyStatus from './pages/ApplyStatus';
import Contact from './pages/Contact';
import Landing from './pages/Landing';
import Features from './pages/Features';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Help from './pages/Help';
import TemplatePreview from './pages/TemplatePreview';
import SitemapPage from './pages/SitemapPage';

// EZP 供貨站頁面
import EzpLayout from './components/EzpLayout';
import EzpHome from './pages/ezp/EzpHome';
import EzpProductList from './pages/ezp/EzpProductList';
import EzpProductDetail from './pages/ezp/EzpProductDetail';
import EzpPartner from './pages/ezp/EzpPartner';

// 平台管理頁面
import PlatformLogin from './pages/platform/PlatformLogin';
import PlatformDashboard from './pages/platform/PlatformDashboard';
import PlatformTenants from './pages/platform/PlatformTenants';
import PlatformApplications from './pages/platform/PlatformApplications';
import PlatformActivations from './pages/platform/PlatformActivations';

// 後台頁面
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminSettings from './pages/admin/AdminSettings';
import AdminCategories from './pages/admin/AdminCategories';
import NotificationSettings from './pages/admin/NotificationSettings';
import AdminRemarketing from './pages/admin/AdminRemarketing';
import AdminMembers from './pages/admin/AdminMembers';
import TenantSettings from './pages/admin/TenantSettings';
import ImportProducts from './pages/admin/ImportProducts';
import Billing from './pages/admin/Billing';
import AdminManual from './pages/admin/AdminManual';

// 平台管理預設主題（不受租戶模板影響）
const platformTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0F766E' },
    secondary: { main: '#E76F51' },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
  },
  shape: {
    borderRadius: 14,
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Noto Sans TC", "Noto Sans JP", "Noto Sans KR", sans-serif',
  },
});

// 平台頁面包裝器：使用獨立主題，覆蓋 StoreProvider 的 ThemeProvider
const PlatformThemeWrapper = ({ children }) => (
  <ThemeProvider theme={platformTheme}>
    <CssBaseline />
    {children}
  </ThemeProvider>
);

// 後台路由包裝器 (需要登入)
const AdminRoute = ({ children }) => {
  const isLoggedIn = localStorage.getItem('admin_token');
  if (!isLoggedIn) {
    return <Navigate to="/admin" replace />;
  }
  return <AdminLayout>{children}</AdminLayout>;
};

// 偵測是否為 EZP 供貨站
const isEzpSite = () => {
  const host = window.location.hostname || '';
  return host.startsWith('ezp.') || host === 'ezp.urstore.tw';
};

// 智慧首頁：SaaS 主站顯示 Landing，租戶商店顯示 Home，EZP 顯示供貨站
const SmartHome = () => {
  const { tenant, loading } = useStore();

  if (loading) {
    return null; // 等待載入
  }

  // EZP 供貨站
  if (isEzpSite()) {
    return <EzpLayout><EzpHome /></EzpLayout>;
  }

  // 無租戶 = SaaS 平台主站 → 顯示 Landing
  if (!tenant) {
    return <Landing />;
  }

  // 有租戶 = 商店 → 顯示 Home（帶 Layout）
  return <Layout><Home /></Layout>;
};

function App() {
  return (
    <AuthProvider>
      <StoreProvider>
        {/* 動態載入所有追蹤代碼 (GTM, GA4, FB Pixel, TikTok) */}
        <TrackingProvider>
          <Routes>
            {/* 後台登入頁 (不需要 Layout) */}
            <Route path="/admin" element={<AdminLogin />} />

            {/* 後台路由 (使用 AdminLayout) */}
            <Route path="/admin/dashboard" element={
              <AdminRoute><AdminDashboard /></AdminRoute>
            } />
            <Route path="/admin/products" element={
              <AdminRoute><AdminProducts /></AdminRoute>
            } />
            <Route path="/admin/categories" element={
              <AdminRoute><AdminCategories /></AdminRoute>
            } />
            <Route path="/admin/orders" element={
              <AdminRoute><AdminOrders /></AdminRoute>
            } />
            <Route path="/admin/members" element={
              <AdminRoute><AdminMembers /></AdminRoute>
            } />
            <Route path="/admin/analytics" element={
              <AdminRoute><AdminAnalytics /></AdminRoute>
            } />
            <Route path="/admin/settings" element={
              <AdminRoute><AdminSettings /></AdminRoute>
            } />
            <Route path="/admin/notifications" element={
              <AdminRoute><NotificationSettings /></AdminRoute>
            } />
            <Route path="/admin/remarketing" element={
              <AdminRoute><AdminRemarketing /></AdminRoute>
            } />
            <Route path="/admin/tenant-settings" element={
              <AdminRoute><TenantSettings /></AdminRoute>
            } />
            <Route path="/admin/import" element={
              <AdminRoute><ImportProducts /></AdminRoute>
            } />
            <Route path="/admin/billing" element={
              <AdminRoute><Billing /></AdminRoute>
            } />
            <Route path="/admin/manual" element={
              <AdminRoute><AdminManual /></AdminRoute>
            } />

            {/* 首頁：SaaS 主站 → Landing，租戶商店 → Home */}
            <Route path="/" element={<SmartHome />} />
            <Route path="/products" element={<Layout><ProductList /></Layout>} />
            <Route path="/products/:id" element={<Layout><ProductDetail /></Layout>} />
            <Route path="/cart" element={<Layout><ErrorBoundary><Cart /></ErrorBoundary></Layout>} />
            <Route path="/checkout" element={<Layout><ErrorBoundary><Checkout /></ErrorBoundary></Layout>} />
            <Route path="/checkout/complete/:orderNo" element={<Layout><OrderComplete /></Layout>} />
            <Route path="/order/:orderNo" element={<Layout><OrderStatus /></Layout>} />
            <Route path="/shopping-guide" element={<Layout><ShoppingGuide /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/sitemap" element={<Layout><SitemapPage /></Layout>} />
            <Route path="/login" element={<Layout><Login /></Layout>} />
            <Route path="/member" element={<Layout><MemberCenter /></Layout>} />
            <Route path="/auth/line/callback" element={<Layout><Login /></Layout>} />

            {/* EZP 供貨站路由 */}
            {isEzpSite() && (
              <>
                <Route path="/products" element={<EzpLayout><EzpProductList /></EzpLayout>} />
                <Route path="/products/:id" element={<EzpLayout><EzpProductDetail /></EzpLayout>} />
                <Route path="/partner" element={<EzpLayout><EzpPartner /></EzpLayout>} />
              </>
            )}

            {/* SaaS 平台介紹頁 */}
            <Route path="/preview/:templateId" element={<TemplatePreview />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/help" element={<Help />} />

            {/* 平台申請頁面 */}
            <Route path="/apply" element={<Apply />} />
            <Route path="/apply/status" element={<ApplyStatus />} />
            <Route path="/apply/status/:id" element={<ApplyStatus />} />

            {/* 平台管理後台（獨立主題，不受租戶模板影響） */}
            <Route path="/platform" element={<PlatformThemeWrapper><PlatformLogin /></PlatformThemeWrapper>} />
            <Route path="/platform/dashboard" element={<PlatformThemeWrapper><PlatformDashboard /></PlatformThemeWrapper>} />
            <Route path="/platform/tenants" element={<PlatformThemeWrapper><PlatformTenants /></PlatformThemeWrapper>} />
            <Route path="/platform/applications" element={<PlatformThemeWrapper><PlatformApplications /></PlatformThemeWrapper>} />
            <Route path="/platform/activations" element={<PlatformThemeWrapper><PlatformActivations /></PlatformThemeWrapper>} />
          </Routes>
        </TrackingProvider>
      </StoreProvider>
    </AuthProvider>
  );
}

export default App;
